import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as agentsApi from '@/api/agents.api';

export const agentKeys = {
  list: (active: boolean) => ['agents', 'list', active] as const,
  one: (id: string) => ['agents', 'one', id] as const,
  profiles: () => ['project-profiles'] as const,
  releases: () => ['releases'] as const,
};

export function useAgents(active: boolean) {
  return useQuery({ queryKey: agentKeys.list(active), queryFn: () => agentsApi.listAgents(active), refetchInterval: 15000 });
}

export function useAgent(id: string | null) {
  return useQuery({ queryKey: agentKeys.one(id || ''), queryFn: () => agentsApi.getAgent(id!), enabled: !!id, refetchInterval: 10000 });
}

export function useAgentActions() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['agents'] });
  return {
    message: useMutation({ mutationFn: (v: { id: string; text: string }) => agentsApi.messageAgent(v.id, v.text), onSuccess: refresh }),
    control: useMutation({
      mutationFn: (v: { id: string; action: 'pause' | 'resume' | 'cancel' }) => agentsApi.controlAgent(v.id, v.action),
      onSuccess: refresh,
    }),
    dispatch: useMutation({ mutationFn: agentsApi.dispatchAgent, onSuccess: refresh }),
  };
}

export function useAgentProfiles() {
  return useQuery({ queryKey: ['agents', 'profiles'], queryFn: agentsApi.getProfiles, staleTime: 5 * 60 * 1000 });
}

export function useProjectProfiles() {
  return useQuery({ queryKey: agentKeys.profiles(), queryFn: agentsApi.listProfiles });
}

export function useProfileActions() {
  const queryClient = useQueryClient();
  return {
    discover: useMutation({ mutationFn: agentsApi.discoverProfile }),
    check: useMutation({ mutationFn: agentsApi.checkProjectSetup }),
    add: useMutation({
      mutationFn: agentsApi.importProject,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.profiles() }),
    }),
    live: useMutation({
      mutationFn: (v: { project: string; live: boolean }) => agentsApi.setProjectLive(v.project, v.live),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.profiles() }),
    }),
    save: useMutation({
      mutationFn: (v: { project: string; profile: Partial<agentsApi.ProjectProfile> }) => agentsApi.saveProfile(v.project, v.profile),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.profiles() }),
    }),
  };
}

export function useReleases() {
  return useQuery({ queryKey: agentKeys.releases(), queryFn: agentsApi.listReleases });
}
